import { Button } from '@/components/ui/button'
import { Camera } from 'lucide-react'
import { useRef } from 'react'

export interface CameraUploadButtonProps {
  onFileSelected: (file: File) => void
  disabled?: boolean
}

/**
 * Camera upload button for nameplate scanning.
 * Mobile-first component that launches camera on mobile, file picker on desktop.
 *
 * The `capture="environment"` attribute tells mobile browsers to use the rear camera.
 *
 * Note: This component only captures the file - it does NOT upload it.
 * The parent component is responsible for uploading when ready.
 */
export function CameraUploadButton({
  onFileSelected,
  disabled,
}: CameraUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    onFileSelected(file)

    // Reset file input so user can select again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />
      <Button
        type="button"
        variant="thalo"
        size="lg"
        onClick={handleButtonClick}
        disabled={disabled}
        className="w-full"
      >
        <Camera className="h-5 w-5 mr-2" />
        Take Photo of Nameplate
      </Button>
    </>
  )
}